import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiFacebook, FiTwitter, FiInstagram, FiLinkedin } from 'react-icons/fi';
import API_BASE_URL from '../config';

const Superscript = styled.sup`
  font-size: 0.6em;
  font-weight: 700;
  vertical-align: super;
  line-height: 0;
  color: inherit;
  position: relative;
  top: -0.2em;
`;

const FooterContainer = styled.footer`
  background-color: #1f2937;
  color: #d1d5db;
  padding: 60px 0 20px;
  margin-top: 80px;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
`;

const FooterSection = styled.div`
  h3 {
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
  }
  
  ul {
    list-style: none;
  }
  
  li {
    margin-bottom: 12px;
  }
  
  a {
    color: #d1d5db;
    text-decoration: none;
    font-size: 14px;
    transition: color 0.2s ease;
    
    &:hover {
      color: #ffffff;
    }
  }
`;

const NewsletterSection = styled.div`
  h3 {
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
  }
  
  p {
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 10px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #374151;
  border-radius: 6px;
  background-color: #374151;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const NewsletterButton = styled.button`
  padding: 12px 24px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #1d4ed8;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 20px;
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: #374151;
    border-radius: 50%;
    color: #d1d5db;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #2563eb;
      color: white;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #374151;
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 14px;
  color: #9ca3af;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 24px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
  
  a {
    color: #9ca3af;
    text-decoration: none;
    font-size: 14px;
    transition: color 0.2s ease;
    
    &:hover {
      color: #ffffff;
    }
  }
`;

const NewsletterMessage = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  
  &.success {
    background-color: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }
  
  &.error {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
  }
`;

// Contact Modal Styles
const Modal = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background-color: #ffffff;
  margin: 5% auto;
  padding: 40px;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-50px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    margin: 10% auto;
    padding: 30px 20px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  @media (max-width: 480px) {
    margin: 5% auto;
    padding: 20px 15px;
    width: 98%;
  }
`;

const CloseButton = styled.span`
  color: #6b7280;
  float: right;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
  position: absolute;
  top: 15px;
  right: 20px;
  transition: color 0.3s ease;
  line-height: 1;
  
  &:hover {
    color: #2563eb;
  }
`;

const ModalTitle = styled.h2`
  color: #2563eb;
  margin-bottom: 10px;
  font-size: 28px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const ModalSubtitle = styled.p`
  color: #6b7280;
  margin-bottom: 30px;
  font-size: 16px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #1f2937;
  font-weight: 600;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  font-family: inherit;
  box-sizing: border-box;
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  font-family: inherit;
  box-sizing: border-box;
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const FormButton = styled.button`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 44px;
  
  &.primary {
    background-color: #2563eb;
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #1d4ed8;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: #6b7280;
    border: 2px solid #e5e7eb;
    
    &:hover {
      background-color: #f3f4f6;
      color: #374151;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormMessage = styled.div`
  margin-top: 20px;
  padding: 15px;
  border-radius: 6px;
  font-weight: 600;
  text-align: center;
  font-size: 14px;
  
  &.success {
    background-color: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
  }
  
  &.error {
    background-color: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }
`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Contact form state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [contactFormMessage, setContactFormMessage] = useState('');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await fetch(`${API_BASE_URL}/newsletter.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage(data.message || 'Thank you for subscribing to our newsletter!');
        setMessageType('success');
        setEmail(''); // Clear the input
      } else {
        setMessage(data.message || 'Sorry, something went wrong. Please try again later.');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage('Sorry, something went wrong. Please try again later.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleContactInputChange = (e) => {
    setContactFormData({
      ...contactFormData,
      [e.target.name]: e.target.value
    });
    setContactFormMessage('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    setContactFormMessage('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactFormData.name,
          email: contactFormData.email,
          message: contactFormData.message
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setContactFormMessage(data.message || 'Thank you for your message! We\'ll get back to you soon.');
        setContactFormData({ name: '', email: '', message: '' });
        
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowContactModal(false);
          setContactFormMessage('');
        }, 3000);
      } else {
        setContactFormMessage(data.message || 'Sorry, something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setContactFormMessage('Sorry, something went wrong. Please try again later.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          {/* <FooterSection>
            <h3>Shop</h3>
            <ul>
              <li><Link to="/books">All Books</Link></li>
              <li><Link to="/categories">Categories</Link></li>
              <li><Link to="/deals">Deals & Offers</Link></li>
              <li><Link to="/new-releases">New Releases</Link></li>
              <li><Link to="/bestsellers">Bestsellers</Link></li>
            </ul>
          </FooterSection> */}
          
          <FooterSection>
            <h3>Account</h3>
            <ul>
              <li><Link to="/login">Sign In</Link></li>
              {/* <li><Link to="/register">Create Account</Link></li> */}
              <li><Link to="/profile">My Account</Link></li>
              {/* <li><Link to="/orders">Order History</Link></li> */}
              {/* <li><Link to="/wishlist">Wishlist</Link></li> */}
            </ul>
          </FooterSection>
          
          <FooterSection>
            <h3>Support</h3>
            <ul>
              {/* <li><Link to="/help">Help Center</Link></li> */}
              <li>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setShowContactModal(true);
                  }}
                  style={{ color: '#d1d5db', textDecoration: 'none', cursor: 'pointer' }}
                >
                  Contact Us
                </a>
              </li>
              {/* <li><Link to="/shipping">Shipping Info</Link></li> */}
              {/* <li><Link to="/returns">Returns</Link></li> */}
              {/* <li><Link to="/faq">FAQ</Link></li> */}
            </ul>
          </FooterSection>
          
          <FooterSection>
            <h3>Company</h3>
            <ul>
              <li><Link to="/about">About Us</Link></li>
            </ul>
          </FooterSection>
          
          <NewsletterSection>
            <h3>Stay Connected</h3>
            <p>Get the latest book recommendations, deals, and news delivered to your inbox.</p>
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <NewsletterInput
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <NewsletterButton type="submit" disabled={loading}>
                {loading ? 'Subscribing...' : 'Subscribe'}
              </NewsletterButton>
            </NewsletterForm>
            {message && (
              <NewsletterMessage className={messageType}>
                {message}
              </NewsletterMessage>
            )}
            {/* <SocialLinks>
              <a href="#" aria-label="Facebook">
                <FiFacebook size={20} />
              </a>
              <a href="#" aria-label="Twitter">
                <FiTwitter size={20} />
              </a>
              <a href="#" aria-label="Instagram">
                <FiInstagram size={20} />
              </a>
              <a href="#" aria-label="LinkedIn">
                <FiLinkedin size={20} />
              </a>
            </SocialLinks> */}
          </NewsletterSection>
        </FooterGrid>
        
        <FooterBottom>
          <Copyright>
            © 2025 M<Superscript>2</Superscript>eBooks.com — All Rights Reserved
          </Copyright>
          <FooterLinks>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/cookies">Cookie Policy</Link>
          </FooterLinks>
        </FooterBottom>
      </FooterContent>
      
      {/* Contact Form Modal */}
      <Modal 
        isOpen={showContactModal} 
        onClick={(e) => e.target === e.currentTarget && setShowContactModal(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={() => setShowContactModal(false)}>&times;</CloseButton>
          <ModalTitle>Contact M²eBooks</ModalTitle>
          <ModalSubtitle>Get in touch with us for inquiries about our books and services.</ModalSubtitle>
          
          <form onSubmit={handleContactSubmit}>
            <FormGroup>
              <FormLabel htmlFor="contact-name">Name *</FormLabel>
              <FormInput
                type="text"
                id="contact-name"
                name="name"
                value={contactFormData.name}
                onChange={handleContactInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="contact-email">Email *</FormLabel>
              <FormInput
                type="email"
                id="contact-email"
                name="email"
                value={contactFormData.email}
                onChange={handleContactInputChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="contact-message">Message *</FormLabel>
              <FormTextarea
                id="contact-message"
                name="message"
                rows="5"
                value={contactFormData.message}
                onChange={handleContactInputChange}
                required
                placeholder="Tell us about your inquiry..."
              />
            </FormGroup>
            
            <FormActions>
              <FormButton 
                type="button" 
                className="secondary" 
                onClick={() => setShowContactModal(false)}
              >
                Cancel
              </FormButton>
              <FormButton 
                type="submit" 
                className="primary" 
                disabled={isSubmittingContact}
              >
                {isSubmittingContact ? 'Sending...' : 'Send Message'}
              </FormButton>
            </FormActions>
          </form>
          
          {contactFormMessage && (
            <FormMessage className={contactFormMessage.includes('Thank you') ? 'success' : 'error'}>
              {contactFormMessage}
            </FormMessage>
          )}
        </ModalContent>
      </Modal>
    </FooterContainer>
  );
};

export default Footer;
